import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from "motion/react";
import type { Variants } from "motion/react";
import {
  ArrowUpRight,
  CalendarCheck,
  HandCoins,
  Heart,
  Receipt,
  Soup,
  Sparkles,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Section from "../components/Section";
import Seo from "../components/Seo";
import CountUp from "../components/CountUp";
import TarifsDecor from "../components/TarifsDecor";
import {
  easeSoft,
  fadeUp,
  springSnappy,
  springSoft,
  staggerContainer,
  viewportOnce,
} from "../lib/motion";

type CardDef = {
  title: string;
  body: string;
  Icon: LucideIcon;
  pill: string;
  border: string;
  hoverBg: string;
  glow: string;
};

const CARDS: CardDef[] = [
  {
    title: "Forfait d'accueil",
    body: "Fixe et identique tous les mois, défini en fonction du nombre de jours d'accueil. Lissé sur 12 mois, il inclut les fermetures annuelles.",
    Icon: CalendarCheck,
    pill: "bg-sage-soft text-sage-hover",
    border: "border-sage/25 hover:border-sage/50",
    hoverBg: "hover:bg-sage-soft",
    glow: "hover:shadow-[0_24px_50px_-18px_color-mix(in_srgb,var(--color-sage)_60%,transparent)]",
  },
  {
    title: "Repas",
    body: "Livrés chaque jour par notre prestataire Ansamble. Aliments locaux et bio dans la mesure du possible. Menus élaborés par des diététiciens.",
    Icon: Soup,
    pill: "bg-terra-soft text-terra-hover",
    border: "border-terra/25 hover:border-terra/50",
    hoverBg: "hover:bg-terra-soft",
    glow: "hover:shadow-[0_24px_50px_-18px_color-mix(in_srgb,var(--color-terra)_60%,transparent)]",
  },
  {
    title: "Soin",
    body: "Couches et nécessaire pour le change inclus : eau nettoyante, coton, savon et liniment bio.",
    Icon: Heart,
    pill: "bg-blush-soft text-blush-hover",
    border: "border-blush/30 hover:border-blush/55",
    hoverBg: "hover:bg-blush-soft",
    glow: "hover:shadow-[0_24px_50px_-18px_color-mix(in_srgb,var(--color-blush)_60%,transparent)]",
  },
  {
    title: "L'équipe",
    body: "Une équipe en or, qualifiée et motivée, qui accompagne vos enfants avec bienveillance toute la journée.",
    Icon: Sparkles,
    pill: "bg-honey-soft text-honey-hover",
    border: "border-honey/40 hover:border-honey/70",
    hoverBg: "hover:bg-honey-soft",
    glow: "hover:shadow-[0_24px_50px_-18px_color-mix(in_srgb,var(--color-honey)_60%,transparent)]",
  },
];

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 32, scale: 0.96 },
  show: { opacity: 1, y: 0, scale: 1, transition: springSoft },
  hover: { y: -8, transition: springSoft },
};

const iconVariants: Variants = {
  hidden: { scale: 0, rotate: -8, opacity: 0 },
  show: {
    scale: 1,
    rotate: 0,
    opacity: 1,
    transition: { ...springSnappy, delay: 0.14 },
  },
  hover: {
    rotate: [0, -10, 8, -4, 0],
    scale: 1.12,
    transition: { duration: 0.6, ease: "easeInOut" },
  },
};

function TarifCard({ card }: { card: CardDef }) {
  const reduce = useReducedMotion();
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const rotateX = useSpring(useTransform(py, [-0.5, 0.5], [6, -6]), {
    stiffness: 220,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(px, [-0.5, 0.5], [-6, 6]), {
    stiffness: 220,
    damping: 18,
  });

  function handleMove(e: React.MouseEvent<HTMLElement>) {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width - 0.5);
    py.set((e.clientY - r.top) / r.height - 0.5);
  }
  function reset() {
    px.set(0);
    py.set(0);
  }

  const Icon = card.Icon;
  return (
    <motion.article
      variants={cardVariants}
      whileHover="hover"
      onMouseMove={handleMove}
      onMouseLeave={reset}
      style={
        reduce
          ? undefined
          : { rotateX, rotateY, transformPerspective: 900 }
      }
      className={`group relative rounded-3xl border bg-surface p-8 shadow-soft-sm transition-[background-color,box-shadow,border-color] duration-500 ease-out ${card.border} ${card.hoverBg} ${card.glow}`}
    >
      <motion.div
        variants={iconVariants}
        className={`mb-5 grid h-12 w-12 place-items-center rounded-2xl ${card.pill}`}
      >
        <Icon size={24} strokeWidth={1.7} aria-hidden="true" />
      </motion.div>
      <h3 className="mb-3 font-display text-2xl font-semibold tracking-tight text-ink">
        {card.title}
      </h3>
      <p className="leading-relaxed text-ink-soft">{card.body}</p>
    </motion.article>
  );
}

type Aide = {
  id: string;
  Icon: LucideIcon;
  pill: string;
  title: string;
  body: string;
  stats: { value: number; suffix: string; label: string; accent: string }[];
  example?: string;
  link: { href: string; label: string; className: string };
  border: string;
  hoverBg: string;
  glow: string;
};

const AIDES: Aide[] = [
  {
    id: "cmg",
    Icon: HandCoins,
    pill: "bg-sage-soft text-sage-hover",
    title: "Complément de libre choix du mode de garde (CMG)",
    body: "Suite à notre déclaration mensuelle des heures de garde, vous recevez de la CAF chaque mois le versement de la CMG Structure, d'un montant compris entre 718,41 € et 992,13 €, calculé selon vos revenus et le nombre d'enfants à charge.",
    stats: [
      { value: 718, suffix: " €", label: "minimum / mois", accent: "text-sage-hover" },
      { value: 992, suffix: " €", label: "maximum / mois", accent: "text-sage-hover" },
    ],
    link: {
      href: "https://www.caf.fr/allocataires/aides-et-demarches/droits-et-prestations/vie-personnelle/le-complement-de-libre-choix-du-mode-de-garde-cmg",
      label: "En savoir plus sur le CMG (CAF)",
      className: "text-sage-hover",
    },
    border: "border-sage/25 hover:border-sage/50",
    hoverBg: "hover:bg-sage-soft",
    glow: "hover:shadow-[0_24px_50px_-18px_color-mix(in_srgb,var(--color-sage)_60%,transparent)]",
  },
  {
    id: "credit",
    Icon: Receipt,
    pill: "bg-terra-soft text-terra-hover",
    title: "Crédit d'impôt",
    body: "Vous pouvez bénéficier d'une déduction d'impôts de 50 % des sommes versées, dans la limite de 1 750 € par an et par enfant.",
    stats: [
      { value: 50, suffix: " %", label: "des sommes versées", accent: "text-terra-hover" },
      { value: 1750, suffix: " €", label: "par an (plafond)", accent: "text-terra-hover" },
    ],
    example:
      "Exemple : si vous payez 725 €/mois de crèche (déduction CAF faite), votre coût brut annuel sera de 8 700 €/an. L'assiette du crédit d'impôt est de 50 % de ce montant, soit 4 350 €, qui excède le plafond de 1 750 €. Votre crédit d'impôt sera donc de 1 750 €, et votre coût net annuel de frais de crèche sera de 6 950 €/an, soit 579 €/mois.",
    link: {
      href: "https://www.service-public.gouv.fr/particuliers/vosdroits/F8",
      label: "Vérifier votre situation (service-public.fr)",
      className: "text-terra-hover",
    },
    border: "border-terra/25 hover:border-terra/50",
    hoverBg: "hover:bg-terra-soft",
    glow: "hover:shadow-[0_24px_50px_-18px_color-mix(in_srgb,var(--color-terra)_60%,transparent)]",
  },
];

function AideCard({ aide }: { aide: Aide }) {
  const reduce = useReducedMotion();
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const rotateX = useSpring(useTransform(py, [-0.5, 0.5], [5, -5]), {
    stiffness: 220,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(px, [-0.5, 0.5], [-5, 5]), {
    stiffness: 220,
    damping: 18,
  });

  function handleMove(e: React.MouseEvent<HTMLElement>) {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width - 0.5);
    py.set((e.clientY - r.top) / r.height - 0.5);
  }
  function reset() {
    px.set(0);
    py.set(0);
  }

  const Icon = aide.Icon;
  return (
    <motion.article
      variants={cardVariants}
      whileHover="hover"
      onMouseMove={handleMove}
      onMouseLeave={reset}
      style={
        reduce ? undefined : { rotateX, rotateY, transformPerspective: 900 }
      }
      className={`group relative flex flex-col rounded-3xl border bg-surface p-8 shadow-soft-sm transition-[background-color,box-shadow,border-color] duration-500 ease-out ${aide.border} ${aide.hoverBg} ${aide.glow}`}
    >
      <motion.div
        variants={iconVariants}
        className={`mb-5 grid h-12 w-12 place-items-center rounded-2xl ${aide.pill}`}
      >
        <Icon size={24} strokeWidth={1.7} aria-hidden="true" />
      </motion.div>
      <h3 className="mb-3 font-display text-2xl font-semibold tracking-tight text-ink">
        {aide.title}
      </h3>
      <p className="leading-relaxed text-ink-soft">{aide.body}</p>

      <div className="mt-6 grid grid-cols-2 gap-4">
        {aide.stats.map((s) => (
          <div key={s.label} className="rounded-2xl bg-cream p-5">
            <CountUp
              value={s.value}
              suffix={s.suffix}
              className={`font-display text-3xl font-semibold md:text-4xl ${s.accent}`}
            />
            <p className="mt-1 text-sm text-ink-soft">{s.label}</p>
          </div>
        ))}
      </div>

      {aide.example && (
        <p className="mt-6 rounded-2xl bg-cream p-5 text-sm leading-relaxed text-ink-soft">
          {aide.example}
        </p>
      )}

      <a
        href={aide.link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={`mt-auto inline-flex items-center gap-1.5 pt-6 text-sm font-semibold underline-offset-4 transition-colors hover:underline ${aide.link.className}`}
      >
        {aide.link.label}
        <ArrowUpRight size={15} strokeWidth={2} aria-hidden="true" />
      </a>
    </motion.article>
  );
}

export default function Tarifs() {
  return (
    <>
      <Seo
        path="/tarifs"
        title="Tarifs et aides — Le coût d'une place en crèche"
        description="Forfait, repas, soin, équipe : ce qui est inclus dans le tarif La La Land. Aides CAF (CMG entre 718 € et 992 €) et crédit d'impôt jusqu'à 1 750 €/an."
      />
      <Section>
        <motion.header
          className="mb-6 max-w-3xl md:mb-8"
          variants={staggerContainer(0.12)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          <motion.h2
            variants={fadeUp}
            className="font-display text-3xl font-semibold tracking-tight text-ink md:text-5xl"
          >
            Le coût d'une{" "}
            <span className="relative inline-block text-terra">
              place
              <motion.span
                aria-hidden="true"
                className="absolute -bottom-1 left-0 h-[3px] w-full origin-left rounded-full bg-sage/70"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={viewportOnce}
                transition={{ duration: 0.7, ease: easeSoft, delay: 0.35 }}
              />
            </span>{" "}
            en crèche
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mt-4 text-lg leading-relaxed text-ink-soft"
          >
            Le forfait La La Land regroupe l'accueil, les repas, les couches et
            l'équipe. Des aides CAF (CMG) et le crédit d'impôt allègent
            significativement votre coût net.
          </motion.p>
        </motion.header>

        <div className="relative isolate py-8">
          <TarifsDecor />
          <motion.div
            className="relative grid gap-6 md:grid-cols-2"
            variants={staggerContainer(0.1)}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
          >
            {CARDS.map((card) => (
              <TarifCard key={card.title} card={card} />
            ))}
          </motion.div>
        </div>
      </Section>

      <Section title="Aides financières">
        <motion.div
          className="grid gap-6 md:grid-cols-2"
          variants={staggerContainer(0.12)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {AIDES.map((aide) => (
            <AideCard key={aide.id} aide={aide} />
          ))}
        </motion.div>
      </Section>
    </>
  );
}
